import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import {
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Switch,
  TextField
} from '@material-ui/core';
import { orderBy } from 'lodash';
import { Search as IconSearch } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Arrow } from '../../../../components/Arrow';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { Loader } from '../../../../components/Loader';
import { TableToolbar } from '../../../../components/Table';
import {
  IColumn,
  VirtualizedSortableTable
} from '../../../../components/Table/VirtualizedSortable';
import { SortDirection } from '../../../../hooks/useSort';
import { Section } from '../../../../layout/Section';
import { useAllOrgs } from '../../../services/orgs';
import {
  IFeatureDashboardListItem,
  getDashboardData,
  setFeature,
  setFeatureForOrgs
} from './service';

type Props = {};

const Actions = styled('div')`
  > * {
    margin-left: ${(p) => p.theme.spacing()}px;
  }
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Heading = styled('h2')`
  min-width: 300px;
`;

type SortKey = 'id' | 'name' | 'enabled';

type DetailsRow = {
  org: Doc<IOrganization>;
  enabled: boolean;
  toggle: () => Promise<any>;
};

const COLUMNS: IColumn<DetailsRow, SortKey>[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => <div>{d.org.id}</div>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 1
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => d.org.data.name,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 4
  },
  {
    key: 'enabled',
    head: () => 'Enabled',
    cell: (d) => (
      <Switch checked={d.enabled} color="default" onChange={() => d.toggle()} />
    ),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 100,
    flexGrow: 1
  }
];

const sortFn = (
  ds: DetailsRow[],
  sortBy: SortKey,
  direction: SortDirection
): DetailsRow[] => {
  return orderBy(
    ds,
    (d) => {
      if (sortBy === 'enabled') {
        return d.enabled;
      }
      if (sortBy === 'name') {
        return d.org.data.name;
      }
      return d.org.id;
    },
    direction
  );
};

const DashboardItemDetails: React.FC<{
  data: IFeatureDashboardListItem;
  changeOne: (org: Doc<IOrganization>, enabled: boolean) => Promise<any>;
}> = ({ data, changeOne }) => {
  const [term, setTerm] = useState('');
  const { orgs } = data;
  const filteredOrgs = useMemo(() => {
    const orgsWithState = [
      ...orgs.enabled.map((org) => ({
        org,
        enabled: true,
        toggle: () => changeOne(org, false)
      })),
      ...orgs.disabled.map((org) => ({
        org,
        enabled: false,
        toggle: () => changeOne(org, true)
      }))
    ];
    return term
      ? orgsWithState.filter((d) => d.org.data.name.indexOf(term) !== -1)
      : orgsWithState;
  }, [orgs, term, changeOne]);
  return (
    <>
      <TableToolbar>
        <TextField
          autoFocus={true}
          type="search"
          value={term}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch size={16} />
              </InputAdornment>
            )
          }}
          onChange={(ev) => setTerm(ev.target.value)}
        />
      </TableToolbar>
      <VirtualizedSortableTable
        rows={filteredOrgs}
        columns={COLUMNS}
        cellProps={undefined}
        height={500}
        margin="normal"
        sortFn={sortFn}
        initialSortColumn={COLUMNS[2]}
        rowClassNameFn={(doc) => {
          if (!doc) {
            return {};
          }
          return { backgroundColor: doc.enabled ? '#eeffe2' : '#ffd9d9' };
        }}
      />
    </>
  );
};

const DashboardItem: React.FC<{
  data: IFeatureDashboardListItem;
  changeAll: (enabled: boolean) => Promise<any>;
  changeOne: (org: Doc<IOrganization>, enabled: boolean) => Promise<any>;
}> = ({ data, changeAll, changeOne }) => {
  const { feature, counts } = data;
  const [expanded, setExpanded] = useState(false);
  return (
    <Section>
      <Card>
        <CardContent>
          <Header>
            <Heading>{feature.displayName}</Heading>
            <div>
              {feature.category} - {counts.enabled}/{counts.total} - default:{' '}
              {feature.defaultValue ? 'true' : 'false'}
            </div>
            <Actions>
              <ButtonWithPromise
                variant="contained"
                color="secondary"
                onClick={() => changeAll(false)}
                pending="Disabling..."
              >
                Disable for everyone
              </ButtonWithPromise>
              <ButtonWithPromise
                variant="contained"
                color="primary"
                onClick={() => changeAll(true)}
                pending="Enabling..."
              >
                Enable for everyone
              </ButtonWithPromise>
              <IconButton onClick={() => setExpanded((e) => !e)}>
                <Arrow dir={expanded ? 'UP' : 'DOWN'} />
              </IconButton>
            </Actions>
          </Header>
          {feature.description && <p>{feature.description}</p>}
          {expanded && (
            <DashboardItemDetails data={data} changeOne={changeOne} />
          )}
        </CardContent>
      </Card>
    </Section>
  );
};

const Dashboard: React.FC<{
  ds: Doc<IOrganization>[];
}> = ({ ds }) => {
  const data = getDashboardData(ds);
  return (
    <>
      {data.map((d) => (
        <DashboardItem
          key={d.name}
          data={d}
          changeAll={(enabled) => setFeatureForOrgs(ds, d.name, enabled)}
          changeOne={(o, enabled) => setFeature(o, d.name, enabled)}
        />
      ))}
    </>
  );
};

export const PageFeatureFlags: React.FC<Props> = () => {
  const [orgs, loading] = useAllOrgs();
  return (
    <Section>
      {loading && <Loader height={500} />}
      {orgs && <Dashboard ds={orgs} />}
    </Section>
  );
};
